import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, Lock, Download, Trash2, ExternalLink, Gift, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { API_BASE, SUBSCRIBE_URL } from "@/lib/config";

export default function AccountPage() {
  const { user, refresh } = useAuth();

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");

  const [delPw, setDelPw] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");

  // Referral block
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/auth/referrals`).then((r) => setRef(r.data)).catch(() => {});
  }, [user]);

  const referralLink = ref?.referral_code
    ? `${window.location.origin}/welcome?ref=${ref.referral_code}`
    : "";

  const copyReferral = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Select and copy manually.");
    }
  };

  if (!user) return null;

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwErr("");
    if (newPw !== confirmPw) {
      setPwErr("New password confirmation doesn't match.");
      return;
    }
    setPwBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated.");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      await refresh();
    } catch (err) {
      setPwErr(formatApiError(err));
    }
    setPwBusy(false);
  };

  const submitDelete = async () => {
    setDelErr("");
    setDelBusy(true);
    try {
      await axios.delete(`${API_BASE}/auth/me`, { data: { password: delPw } });
      toast.success("Account deleted. Goodbye!");
      // Replace URL synchronously BEFORE clearing the auth context so the
      // /account ProtectedRoute can't re-render with redirectTo=/login first.
      window.location.replace("/welcome");
    } catch (err) {
      setDelErr(formatApiError(err));
      setDelBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--app-bg)]">
      <header className="px-6 py-4 border-b border-[var(--app-border)] bg-white flex items-center gap-4">
        <Link to="/" className="text-sm flex items-center gap-1 text-[var(--app-text-soft)] hover:text-[var(--app-text)]" data-testid="back-to-library">
          <ChevronLeft className="w-4 h-4" /> Library
        </Link>
        <div className="text-sm font-display font-extrabold tracking-tight">Account</div>
      </header>

      <main className="max-w-2xl mx-auto py-10 px-6 space-y-8" data-testid="account-page">
        {/* Profile card */}
        <section className="bg-white border border-[var(--app-border)] rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3">
            Profile
          </div>
          <div className="space-y-1">
            <div className="font-display font-extrabold text-lg" data-testid="account-name">{user.name || "—"}</div>
            <div className="text-sm text-[var(--app-text-soft)]" data-testid="account-email">{user.email}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--app-border)] flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-1">
                Subscription
              </div>
              {user.is_subscribed ? (
                <div className="text-sm" data-testid="subscription-status">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5 align-middle" />
                  Active subscription
                </div>
              ) : user.trial_days_left > 0 ? (
                <div className="text-sm" data-testid="subscription-status">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1.5 align-middle" />
                  Free trial — <span className="font-mono-tech">{user.trial_days_left}</span> day{user.trial_days_left === 1 ? "" : "s"} remaining
                </div>
              ) : (
                <div className="text-sm text-red-600" data-testid="subscription-status">
                  Trial expired — subscribe to keep editing.
                </div>
              )}
            </div>
            {!user.is_subscribed && (
              <a href={SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="manage-subscription">
                  Subscribe <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </a>
            )}
          </div>
        </section>

        {/* Change password */}
        <section className="bg-white border border-[var(--app-border)] rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Change password
          </div>
          <form onSubmit={submitPassword} className="space-y-3" data-testid="change-password-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="cpw">Current password</Label>
              <Input id="cpw" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required data-testid="cpw-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="npw">New password</Label>
              <Input id="npw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required data-testid="npw-input" />
              <div className="text-[10px] text-[var(--app-text-soft)] mt-1">Min 8 characters, with a letter and a number.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="cnpw">Confirm new password</Label>
              <Input id="cnpw" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required data-testid="cnpw-input" />
            </div>
            {pwErr && <div className="text-sm text-red-600" data-testid="pw-error">{pwErr}</div>}
            <Button type="submit" disabled={pwBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="cpw-submit">
              {pwBusy ? "Saving…" : "Update password"}
            </Button>
          </form>
        </section>

        {/* Refer a breeder */}
        <section className="bg-white border border-[var(--app-border)] rounded-md p-6" data-testid="referral-section">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3 flex items-center gap-1.5">
            <Gift className="w-3 h-3" /> Refer a breeder · get a free month
          </div>
          <p className="text-sm text-[var(--app-text-soft)] mb-4 leading-relaxed">
            Share your link with another breeder. When they subscribe, we'll
            add <span className="font-mono-tech text-[var(--app-text)]">30 days</span> to
            your account at no charge.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              readOnly
              value={referralLink || "Generating…"}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 px-3 py-2 rounded border border-[var(--app-border)] bg-[var(--app-bg)] font-mono-tech text-xs text-[var(--app-text)] focus:outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="referral-link"
            />
            <Button variant="outline" size="sm" onClick={copyReferral} disabled={!referralLink} data-testid="referral-copy">
              {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          {ref?.referral_code && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[var(--app-text-soft)] font-mono-tech" data-testid="referral-code-readable">
              Code: <span className="text-[var(--app-text)]">{ref.referral_code}</span>
            </div>
          )}
          {ref && (
            <div className="mt-4 flex flex-wrap gap-6 text-xs text-[var(--app-text-soft)]" data-testid="referral-stats">
              <div>
                Referred:{" "}
                <span className="font-mono-tech text-[var(--app-text)]">{ref.referred_count}</span>
              </div>
              <div>
                Subscribed:{" "}
                <span className="font-mono-tech text-[var(--app-text)]">{ref.subscribed_count}</span>
              </div>
              <div>
                Credit earned:{" "}
                <span className="font-mono-tech text-[var(--app-text)]">{ref.credits_days}</span>{" "}
                day{ref.credits_days === 1 ? "" : "s"}
              </div>
            </div>
          )}
        </section>

        {/* Data export */}
        <section className="bg-white border border-[var(--app-border)] rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3 flex items-center gap-1.5">
            <Download className="w-3 h-3" /> Your data
          </div>
          <p className="text-sm text-[var(--app-text-soft)] mb-4 leading-relaxed">
            Download a complete copy of your library and breeding records at any time —
            even after your subscription ends.
          </p>
          <div className="flex flex-wrap gap-2">
            <a href={`${API_BASE}/exports/animals.csv`} download="vitality-pedigree-library.csv" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-animals">
                <Download className="w-4 h-4 mr-1.5" /> Animals CSV
              </Button>
            </a>
            <a href={`${API_BASE}/exports/breedings.csv`} download="vitality-pedigree-breedings.csv" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-breedings">
                <Download className="w-4 h-4 mr-1.5" /> Breedings CSV
              </Button>
            </a>
            <a href={`${API_BASE}/exports/animals.xlsx`} download="vitality-pedigree-library.xlsx" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-xlsx">
                <Download className="w-4 h-4 mr-1.5" /> Library XLSX
              </Button>
            </a>
          </div>
        </section>

        {/* Danger zone */}
        <section className="bg-white border border-red-200 rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-red-600 mb-3 flex items-center gap-1.5">
            <Trash2 className="w-3 h-3" /> Danger zone
          </div>
          <p className="text-sm text-[var(--app-text-soft)] mb-4 leading-relaxed">
            Deleting your account will permanently remove every animal, breeding,
            photo, and measurement you've recorded. This cannot be undone.
            Consider exporting your data first.
          </p>
          <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDelOpen(true)} data-testid="delete-account">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete my account
          </Button>
        </section>
      </main>

      <AlertDialog open={delOpen} onOpenChange={(o) => { setDelOpen(o); if (!o) { setDelPw(""); setDelErr(""); } }}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              All your animals, breedings, photos and measurements will be permanently
              removed. Enter your password to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="delpw">Password</Label>
            <Input id="delpw" type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} data-testid="delete-account-password" />
            {delErr && <div className="text-sm text-red-600" data-testid="delete-account-error">{delErr}</div>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); submitDelete(); }}
              disabled={delBusy || !delPw}
              data-testid="delete-account-confirm"
            >
              {delBusy ? "Deleting…" : "Permanently delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
